import { ExternalLink, Eye, ImageOff, RefreshCw, Tags } from "lucide-react";
import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";

import type { GroupedItemDetailDto, GroupedItemDto, ProductDto } from "../../../shared/types";
import { ClassifyModal } from "../../components/ClassifyModal";
import { variantState } from "../../components/ItemTile";
import { fmtGone } from "../../lib/format";
import { proxiedImageUrl } from "../../lib/imageProxy";
import { clipToRange, STRIP_RANGE_MS } from "../../lib/observations";
import {
	BackLink,
	Button,
	EmptyState,
	ErrorState,
	Money,
	Page,
	Row,
	SectionTitle,
	Skeleton,
	Spinner,
	StateLabel,
} from "../../ui";
import { GroupingActions } from "./GroupingActions";
import { type GroupHistory, type Range, RANGES, totalSamplesFor } from "./history";
import { StoreInventoryPanel } from "./StoreInventoryPanel";

// recharts is ~370 KB minified and this is the only page that draws with it,
// so the charts load as their own chunk rather than sitting in the entry
// every page downloads. Everything above imports from "./history", which is
// recharts-free — importing a value from HistoryCharts here would defeat it.
const HistoryCharts = lazy(() => import("./HistoryCharts"));

async function fetchProduct(id: string): Promise<ProductDto> {
	const res = await fetch(`/api/products/${encodeURIComponent(id)}`);
	if (!res.ok) {
		const data = (await res.json().catch(() => ({}))) as { error?: string };
		throw new Error(data.error ?? `HTTP ${res.status}`);
	}
	const data = (await res.json()) as { product: ProductDto };
	return data.product;
}

// findProductWithLatest (backing /api/products/:id) has no removed_at
// filter, but findGroupVariants (backing this endpoint) does — so a
// delisted product can 200 on the first fetch and 404 on this one, when its
// group has no remaining active variants. That's not "this product doesn't
// exist"; distinguish it from every other failure (network error, 500) so
// the caller can render the product on its own instead of erroring.
class GroupNotFoundError extends Error {}

/** The window InStockStrip is pinned to, as a chart Range. Kept alongside
 *  STRIP_RANGE_MS (its millisecond twin, used to clip the rows once they
 *  arrive) so the fetch and the clip can't drift apart. */
const STRIP_RANGE: Range = "30d";

async function fetchGroup(groupKey: string): Promise<GroupedItemDetailDto> {
	const res = await fetch(`/api/items/group/${encodeURIComponent(groupKey)}`);
	if (res.status === 404) {
		throw new GroupNotFoundError("group not found");
	}
	if (!res.ok) {
		const data = (await res.json().catch(() => ({}))) as { error?: string };
		throw new Error(data.error ?? `HTTP ${res.status}`);
	}
	return (await res.json()) as GroupedItemDetailDto;
}

async function fetchGroupHistory(groupKey: string, range: Range): Promise<GroupHistory> {
	const res = await fetch(`/api/items/group/${encodeURIComponent(groupKey)}/history?range=${range}`);
	if (!res.ok) {
		return { rangeMs: 0, sinceMs: 0, variants: [] };
	}
	return (await res.json()) as GroupHistory;
}

/** A single-variant stand-in for GroupedItemDto, used only when the real
 *  group lookup 404s (see GroupNotFoundError above) — lets the summary and
 *  the Row list render this product on its own rather than the page
 *  erroring for a product that does exist. */
function syntheticGroup(product: ProductDto): GroupedItemDto {
	const priceEntry =
		product.priceMinor != null
			? {
					productId: product.id,
					storeId: product.storeId,
					priceMinor: product.priceMinor,
				}
			: null;
	return {
		groupKey: product.groupKey,
		productId: product.id,
		ean: product.ean,
		title: product.title,
		imageUrl: product.imageUrl,
		brand: product.brand,
		variantCount: 1,
		storeCount: 1,
		inStockCount: product.inStock === true ? 1 : 0,
		storeIds: [product.storeId],
		maxLastSeenAt: product.lastSeenAt,
		anyWatched: product.isWatched,
		cheapestInStock: product.inStock === true ? priceEntry : null,
		cheapestOverall: priceEntry,
		lastInStock: null,
	};
}

// The server's own reasons ("not found", "HTTP 404") are for logs, not the
// operator — lead with a stable sentence, keep the raw reason as detail.
function productLoadError(e: unknown): string {
	const reason = (e instanceof Error && e.message) || "request failed";
	return reason === "not found"
		? "This product doesn't exist or was removed."
		: `Couldn't load this product (${reason}).`;
}

/**
 * One product, resolved to its group: a summary, one shelf-label Row per
 * store variant, the price trend, the stock swimlane, and the grouping
 * actions that concern this group. Replaces the old two-screen split
 * (/items/:id for a single listing, /items/group/:groupKey for the group) —
 * every internal link already points at a product id, so this page resolves
 * the group itself rather than asking the caller to know which key to use.
 */
export function ProductPage({ id }: { id: string }) {
	const [groupKey, setGroupKey] = useState<string | null>(null);
	const [group, setGroup] = useState<GroupedItemDto | null>(null);
	const [groupAvailable, setGroupAvailable] = useState(true);
	const [variants, setVariants] = useState<ProductDto[]>([]);
	const [viewedProduct, setViewedProduct] = useState<ProductDto | null>(null);
	const [loading, setLoading] = useState(true);
	const [refreshing, setRefreshing] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [range, setRange] = useState<Range>("30d");
	const [classifyOpen, setClassifyOpen] = useState(false);
	// Stable while open: the page re-renders on live updates, and a new object
	// each time would make the dialog classify all over again.
	const classifyTarget = useMemo(
		() => (classifyOpen && group ? { groupKey: group.groupKey, title: group.title } : null),
		[classifyOpen, group?.groupKey, group?.title],
	);
	const [history, setHistory] = useState<GroupHistory | null>(null);
	const [historyLoading, setHistoryLoading] = useState(false);
	// Fixed at 30 days regardless of the chart's own range toggle: rows stay
	// comparable with each other (the same reason lib/observations.ts's
	// STRIP_RANGE_MS is fixed rather than per-product), and InStockStrip's own
	// tooltip claims "last 30 days" — following the chart range would make
	// that claim false the moment someone taps "24h".
	const [stripHistory, setStripHistory] = useState<GroupHistory | null>(null);

	const load = useCallback(
		async (mode: "initial" | "refresh") => {
			if (mode === "initial") setLoading(true);
			else setRefreshing(true);
			setError(null);
			try {
				const product = await fetchProduct(id);
				setViewedProduct(product);
				setGroupKey(product.groupKey);
				try {
					const data = await fetchGroup(product.groupKey);
					setGroup(data.group);
					setVariants(data.variants);
					setGroupAvailable(true);
				} catch (e) {
					if (!(e instanceof GroupNotFoundError)) throw e;
					setGroup(syntheticGroup(product));
					setVariants([product]);
					setGroupAvailable(false);
				}
			} catch (e) {
				setError(productLoadError(e));
			} finally {
				if (mode === "initial") setLoading(false);
				else setRefreshing(false);
			}
		},
		[id],
	);

	useEffect(() => {
		void load("initial");
	}, [load]);

	// One effect for both series. The strip is pinned to STRIP_RANGE while the
	// chart follows `range`, so whenever the chart is on 30d too — its default,
	// and so the case that every product page opens in — the two want
	// byte-identical responses. Fetching them from separate effects meant two
	// identical /history requests per page open, each resolving the group again
	// server-side. Ask for the second range only when it differs.
	useEffect(() => {
		if (!groupKey || !groupAvailable) {
			setHistory(null);
			setStripHistory(null);
			return;
		}
		let cancelled = false;
		setHistoryLoading(true);
		const wanted: Range[] = range === STRIP_RANGE ? [range] : [range, STRIP_RANGE];
		Promise.all(wanted.map((r) => fetchGroupHistory(groupKey, r)))
			.then(([main, strip]) => {
				if (cancelled || !main) return;
				setHistory(main);
				setStripHistory(strip ?? main);
			})
			.finally(() => {
				if (!cancelled) setHistoryLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [groupKey, range, groupAvailable]);

	if (loading) {
		return (
			<Page>
				<div className="flex items-center gap-2 py-6 text-[13px] text-muted">
					<Spinner /> Loading…
				</div>
			</Page>
		);
	}
	if (error || !group || !groupKey) {
		return (
			<Page width="narrow" back={<BackLink href="#/items">Items</BackLink>}>
				<ErrorState
					message={error ?? "This product doesn't exist or was removed."}
					onRetry={error ? () => void load("initial") : undefined}
				/>
			</Page>
		);
	}

	const now = Date.now();
	const cis = group.cheapestInStock;
	const co = group.cheapestOverall;
	const sortedVariants = [...variants].sort((a, b) => {
		const aIs = a.inStock === true ? 1 : 0;
		const bIs = b.inStock === true ? 1 : 0;
		if (aIs !== bIs) return bIs - aIs;
		return (a.priceMinor ?? Infinity) - (b.priceMinor ?? Infinity);
	});
	const observationsFor = (productId: number) => {
		const obs = stripHistory?.variants.find((v) => v.productId === productId)?.observations ?? [];
		// Belt-and-braces: even though the fetch above always asks for 30d,
		// clip explicitly rather than trust the server's window to line up
		// exactly with STRIP_RANGE_MS — this is the one place in pages/product
		// that consumes clipToRange directly, guaranteeing the strip's own
		// "last 30 days" claim stays true regardless of what range produced
		// the underlying data.
		return clipToRange(obs, now, STRIP_RANGE_MS);
	};

	// EAN is the basis of the group key and the field grouping decisions turn
	// on, so it (and brand) come from the group when known — falling back to
	// the specifically-viewed product only in the synthetic single-variant
	// case, where there is no group-level value to prefer.
	const detailEan = group.ean ?? viewedProduct?.ean ?? null;
	const detailBrand = group.brand ?? viewedProduct?.brand ?? null;
	const categories = viewedProduct?.categories ?? [];
	const description = viewedProduct?.description ?? null;

	return (
		<Page back={<BackLink href="#/items">Items</BackLink>}>
			<ClassifyModal group={classifyTarget} onClose={() => setClassifyOpen(false)} />

			<div className="grid gap-x-6 gap-y-4 lg:grid-cols-[300px_minmax(0,1fr)] xl:grid-cols-[340px_minmax(0,1fr)]">
				{/* The product itself — what it is, what it costs, where it's sold.
            Sticky from lg so the answer stays beside the charts. */}
				<aside className="lg:sticky lg:top-16 lg:self-start">
					<ProductSummary
						group={group}
						now={now}
						brand={detailBrand}
						ean={detailEan}
						categories={categories}
						description={description}
						refreshing={refreshing}
						onRefresh={() => void load("refresh")}
						onClassify={() => setClassifyOpen(true)}
					/>
					{!groupAvailable && (
						<p className="mt-2 rounded-[10px] border border-hairline bg-paper px-3 py-2 text-xs text-muted">
							This listing's group isn't available right now — showing what's known about this product on its own.
						</p>
					)}
				</aside>

				<div className="flex min-w-0 flex-col gap-5">
					<section>
						<SectionTitle aside={<span className="text-xs text-muted">{sortedVariants.length}</span>}>
							Stores
						</SectionTitle>
						<ul className="grid gap-1 xl:grid-cols-2">
							{sortedVariants.map((v) => (
								<li key={v.id}>
									<Row
										title={v.title}
										store={v.storeId}
										state={variantState(v.inStock)}
										priceMinor={v.priceMinor}
										stateLabel={
											cis?.productId === v.id
												? "Cheapest in stock"
												: co?.productId === v.id
													? "Cheapest overall"
													: undefined
										}
										meta={v.sku ? `SKU ${v.sku}` : undefined}
										observations={observationsFor(v.id)}
										now={now}
										action={
											<a
												href={v.url}
												target="_blank"
												rel="noreferrer noopener"
												className="flex w-9 items-center justify-center rounded-lg text-muted hover:bg-paper hover:text-ink"
												title="Open at store"
												aria-label="Open at store"
											>
												<ExternalLink className="size-4" aria-hidden />
											</a>
										}
									/>
								</li>
							))}
						</ul>
					</section>

					<section>
						<SectionTitle
							aside={
								<>
									<span className="text-xs text-muted">
										{historyLoading
											? "Loading…"
											: history
												? `${totalSamplesFor(history)} samples across ${history.variants.length} ${
														history.variants.length === 1 ? "store" : "stores"
													}`
												: ""}
									</span>
									<div
										role="group"
										aria-label="Range"
										className="ml-auto inline-flex rounded-lg border border-hairline bg-ground p-0.5"
									>
										{RANGES.map((r) => (
											<button
												key={r.key}
												type="button"
												aria-pressed={range === r.key}
												onClick={() => setRange(r.key)}
												className={`min-h-tap rounded-md px-2.5 text-xs sm:h-6 sm:min-h-0 ${
													range === r.key
														? "bg-paper font-semibold text-ink shadow-[0_1px_2px_rgba(6,9,18,0.12)]"
														: "text-muted hover:text-ink"
												}`}
											>
												{r.label}
											</button>
										))}
									</div>
								</>
							}
						>
							Trend
						</SectionTitle>

						{history && history.variants.length > 0 ? (
							// 273px is the price chart card at rest (220px plot + header and
							// padding), so the swap in reserves roughly the right space.
							<Suspense fallback={<Skeleton className="w-full" height={273} />}>
								<HistoryCharts history={history} range={range} />
							</Suspense>
						) : (
							<EmptyState title="No history yet for this range." />
						)}
					</section>

					<StoreInventoryPanel productId={Number(id)} />

					<GroupingActions
						groupKey={groupKey}
						productIds={variants.map((v) => v.id)}
						groupEan={detailEan}
						onChanged={() => void load("refresh")}
					/>
				</div>
			</div>
		</Page>
	);
}

/**
 * What the product is and the one price that matters. Carries no state
 * stripe for the same reason the old GroupHeader didn't: a group has a set of
 * states across its stores, and the stripes live on the store Rows.
 */
function ProductSummary({
	group,
	now,
	brand,
	ean,
	categories,
	description,
	refreshing,
	onRefresh,
	onClassify,
}: {
	group: GroupedItemDto;
	now: number;
	brand: string | null;
	ean: string | null;
	categories: string[];
	description: string | null;
	refreshing: boolean;
	onRefresh: () => void;
	onClassify: () => void;
}) {
	const [imageFailed, setImageFailed] = useState(false);
	const src = proxiedImageUrl(group.imageUrl);
	const live = group.cheapestInStock;
	const head = live
		? {
				minor: live.priceMinor,
				state: "live" as const,
				label: `In stock at ${group.inStockCount} of ${group.storeCount}`,
			}
		: group.lastInStock
			? {
					minor: group.lastInStock.priceMinor,
					state: "gone" as const,
					label: `Gone ${fmtGone(group.lastInStock.at, now)}`,
				}
			: { minor: group.cheapestOverall?.priceMinor ?? null, state: "idle" as const, label: "Never in stock" };

	return (
		<div
			className={`overflow-hidden rounded-[14px] border ${
				head.state === "live" ? "border-foil" : "border-hairline bg-paper"
			}`}
		>
			<div className="grid grid-cols-[96px_minmax(0,1fr)] gap-3 p-2 lg:block lg:p-0">
				<div className="relative aspect-square overflow-hidden rounded-[10px] bg-plate lg:m-1.5 lg:mb-0 lg:aspect-[4/3]">
					{src && !imageFailed ? (
						<img
							src={src}
							alt=""
							onError={() => setImageFailed(true)}
							className="size-full object-contain p-2 mix-blend-multiply lg:p-4"
						/>
					) : (
						<span className="flex size-full items-center justify-center text-[#566079]">
							<ImageOff className="size-6" aria-hidden />
						</span>
					)}
					{group.anyWatched && (
						<span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-ink px-2 py-0.5 text-[11px] font-medium text-ground">
							<Eye className="size-3" aria-hidden />
							Watched
						</span>
					)}
				</div>

				<div className="min-w-0 lg:px-3.5 lg:pt-3 lg:pb-3.5">
					<h1 className="text-[19px] leading-6 font-bold font-tag text-balance lg:text-[22px] lg:leading-7">
						{group.title}
					</h1>
					<div className="mt-2 flex items-end justify-between gap-3">
						<div>
							<Money
								minor={head.minor}
								className={`block text-[26px] leading-7 ${head.state === "live" ? "" : "text-muted"}`}
							/>
							<StateLabel state={head.state} label={head.label} />
						</div>
						<div className="flex shrink-0 items-center gap-0.5">
							<Button
								variant="ghost"
								size="sm"
								onClick={onClassify}
								title="Classify this item against the official set list"
							>
								<Tags className="size-3.5" aria-hidden />
								Classify
							</Button>
							<Button variant="ghost" size="sm" onClick={onRefresh} disabled={refreshing}>
								<RefreshCw className={`size-3.5 ${refreshing ? "animate-spin" : ""}`} aria-hidden />
								Refresh
							</Button>
						</div>
					</div>
				</div>
			</div>

			{(brand || ean || categories.length > 0 || description) && (
				<div className="flex flex-col gap-2 border-t border-hairline px-3.5 py-3">
					{(brand || ean) && (
						<dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs">
							{brand && (
								<>
									<dt className="text-muted">Brand</dt>
									<dd className="truncate text-ink">{brand}</dd>
								</>
							)}
							{ean && (
								<>
									<dt className="text-muted">EAN</dt>
									<dd className="font-mono text-[11px] text-ink">{ean}</dd>
								</>
							)}
						</dl>
					)}
					{categories.length > 0 && (
						<ul className="flex flex-wrap gap-1" aria-label="Categories">
							{categories.map((c) => (
								<li key={c} className="rounded-full bg-raised px-2 py-0.5 text-[11px] text-ink">
									{c}
								</li>
							))}
						</ul>
					)}
					{description && <Description text={description} />}
				</div>
			)}
		</div>
	);
}

/** Store blurbs run long; four lines, then the rest on request. */
function Description({ text }: { text: string }) {
	const [open, setOpen] = useState(false);
	return (
		<div className="text-xs leading-relaxed text-muted">
			<p className={open ? "" : "line-clamp-4"}>{text}</p>
			{text.length > 240 && (
				<button
					type="button"
					onClick={() => setOpen((v) => !v)}
					className="mt-0.5 font-medium text-ink hover:underline"
				>
					{open ? "Show less" : "Show more"}
				</button>
			)}
		</div>
	);
}
